import React from 'react';

function parseInline(text, keyPrefix = 'i') {
  if (!text) return [];
  const nodes = [];
  const re = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(`([^`]+)`)/g;
  const matches = [...text.matchAll(re)];
  let lastIdx = 0;
  let keyIdx = 0;
  for (const match of matches) {
    if (match.index > lastIdx) {
      nodes.push(text.slice(lastIdx, match.index));
    }
    const key = `${keyPrefix}-${keyIdx++}`;
    if (match[1]) {
      nodes.push(<a key={key} href={match[3]} target="_blank" rel="noopener noreferrer">{match[2]}</a>);
    } else if (match[4]) {
      nodes.push(<strong key={key}>{match[5]}</strong>);
    } else if (match[6]) {
      nodes.push(<em key={key}>{match[7]}</em>);
    } else if (match[8]) {
      nodes.push(<code key={key}>{match[9]}</code>);
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    nodes.push(text.slice(lastIdx));
  }
  return nodes;
}

function parseMarkdown(md) {
  if (!md) return [];
  const lines = String(md).split('\n');
  const blocks = [];
  let blockKey = 0;
  let paragraph = [];
  let listItems = [];
  let orderedItems = [];
  let blockquoteLines = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      const text = paragraph.join(' ').trim();
      if (text) {
        blocks.push(<p key={`p-${blockKey++}`}>{parseInline(text, `p${blockKey}`)}</p>);
      }
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push(<ul key={`ul-${blockKey++}`}>{listItems.map((it, i) => (
        <li key={i}>{parseInline(it, `ul${blockKey}-${i}`)}</li>
      ))}</ul>);
      listItems = [];
    }
  };
  const flushOrdered = () => {
    if (orderedItems.length > 0) {
      blocks.push(<ol key={`ol-${blockKey++}`}>{orderedItems.map((it, i) => (
        <li key={i}>{parseInline(it, `ol${blockKey}-${i}`)}</li>
      ))}</ol>);
      orderedItems = [];
    }
  };
  const flushBlockquote = () => {
    if (blockquoteLines.length > 0) {
      blocks.push(<blockquote key={`bq-${blockKey++}`}>{blockquoteLines.map((it, i) => (
        <p key={i}>{parseInline(it, `bq${blockKey}-${i}`)}</p>
      ))}</blockquote>);
      blockquoteLines = [];
    }
  };
  const flushAll = () => { flushParagraph(); flushList(); flushOrdered(); flushBlockquote(); };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') { flushAll(); continue; }

    if (line.startsWith('### ')) {
      flushAll();
      blocks.push(<h3 key={`h3-${blockKey++}`}>{parseInline(line.substring(4).trim(), `h3${blockKey}`)}</h3>);
      continue;
    }
    if (line.startsWith('## ')) {
      flushAll();
      blocks.push(<h2 key={`h2-${blockKey++}`}>{parseInline(line.substring(3).trim(), `h2${blockKey}`)}</h2>);
      continue;
    }
    if (line.startsWith('# ')) {
      flushAll();
      blocks.push(<h1 key={`h1-${blockKey++}`}>{parseInline(line.substring(2).trim(), `h1${blockKey}`)}</h1>);
      continue;
    }
    if (line.trim() === '---' || line.trim() === '***') {
      flushAll();
      blocks.push(<hr key={`hr-${blockKey++}`} />);
      continue;
    }
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      flushParagraph(); flushOrdered(); flushBlockquote();
      listItems.push(bulletMatch[1]);
      continue;
    }
    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph(); flushList(); flushBlockquote();
      orderedItems.push(orderedMatch[1]);
      continue;
    }
    if (line.startsWith('> ')) {
      flushParagraph(); flushList(); flushOrdered();
      blockquoteLines.push(line.substring(2));
      continue;
    }
    flushList(); flushOrdered(); flushBlockquote();
    paragraph.push(line);
  }
  flushAll();
  return blocks;
}

const darkStyles = `
.md-render { color: #cbd5e1; line-height: 1.75; }
.md-render h1 { font-size: 28px; font-weight: 700; margin: 32px 0 16px; color: #ffffff; letter-spacing: -0.01em; }
.md-render h2 { font-size: 22px; font-weight: 700; margin: 32px 0 14px; color: #ffffff; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.08); letter-spacing: -0.005em; }
.md-render h3 { font-size: 18px; font-weight: 600; margin: 24px 0 10px; color: #ffffff; }
.md-render p { margin: 14px 0; color: #cbd5e1; }
.md-render ul, .md-render ol { margin: 14px 0 14px 24px; padding-left: 12px; }
.md-render ul li { list-style: disc; margin: 6px 0; color: #cbd5e1; }
.md-render ol li { list-style: decimal; margin: 6px 0; color: #cbd5e1; }
.md-render blockquote { margin: 20px 0; padding: 12px 18px; border-left: 4px solid #0099D6; background: rgba(0,153,214,0.08); color: #e2e8f0; font-style: italic; border-radius: 0 8px 8px 0; }
.md-render blockquote p { margin: 6px 0; color: #e2e8f0; }
.md-render a { color: #0099D6; text-decoration: underline; text-underline-offset: 2px; }
.md-render a:hover { color: #38bdf8; }
.md-render strong { font-weight: 700; color: #ffffff; }
.md-render em { font-style: italic; }
.md-render code { background: rgba(255,255,255,0.06); padding: 2px 6px; border-radius: 4px; font-family: 'Fira Code', 'Courier New', monospace; font-size: 13px; color: #38bdf8; }
.md-render hr { border: 0; border-top: 1px solid rgba(255,255,255,0.08); margin: 32px 0; }
`;

export default function MarkdownView({ markdown, className, inline = false }) {
  if (inline) {
    return <span className={className}>{parseInline(markdown || '', 'inline')}</span>;
  }
  const blocks = parseMarkdown(markdown || '');
  return (
    <>
      <style>{darkStyles}</style>
      <div className={`md-render ${className || ''}`}>{blocks}</div>
    </>
  );
}
